import { ColDef, GridApi } from 'ag-grid-community';
import { getColDefs } from './helpers/colDefinitions';
import {
    BudgetProposalT,
    FieldSettingsT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import { getColumnSettings } from './helpers/gridHelpers';
import {
    getDefaultGridColumnShowing,
    getDefaultGridColumnsOrdering,
    getExcludedGridColumnFromFilterPanel,
} from '../gridHelpers/defaultGridColumnsConfiguration';
import { Phase } from 'business/constants';

type Props = {
    budgetProposal: BudgetProposalT;
    gridApi: GridApi | null;
    imputationDimensions: string[];
    primaryColor: string;
    statusesSettings: StatusesSettingsT;
    phasesSettings: Phase[];
    t: TranslationT;
    columnsSettings: FieldSettingsT[];
    isSchool: boolean;
    softwares: Set<string>;
};

/**
 * Generates and configures the column definitions for the imputations grid based on the provided budget proposal
 * and various settings.
 *
 * @returns {ColDef[]} - The configured column definitions for the imputations grid.
 *
 */
const getImputationsColumns = ({
    budgetProposal,
    gridApi,
    imputationDimensions,
    primaryColor,
    statusesSettings,
    phasesSettings,
    t,
    columnsSettings,
    isSchool,
    softwares,
}: Props): ColDef[] => {
    const columns: ColDef[] = getColDefs(
        budgetProposal,
        gridApi,
        imputationDimensions,
        primaryColor,
        statusesSettings,
        phasesSettings,
        t,
        softwares,
    );
    const defaultGridColumnShown = getDefaultGridColumnShowing(
        budgetProposal.exercice,
    );

    // we want to exclude some columns for SCHOOL software as they dont make sense for them
    const excludedGridColumnFromFilterPanel =
        getExcludedGridColumnFromFilterPanel(budgetProposal.exercice, isSchool);

    const shownColumns = columns.map((col) => {
        const updatedCol =
            !getColumnSettings(col, columnsSettings) &&
            col.field &&
            !defaultGridColumnShown.includes(col.field)
                ? { ...col, hide: true }
                : col;
        if (
            col.field &&
            excludedGridColumnFromFilterPanel.includes(col.field)
        ) {
            updatedCol.suppressColumnsToolPanel = true;
        }
        return updatedCol;
    });

    const defaultGridColumnsOrdering = getDefaultGridColumnsOrdering(
        budgetProposal.exercice,
    );

    shownColumns.sort((a, b) => {
        const aSettings = getColumnSettings(a, columnsSettings);
        const bSettings = getColumnSettings(b, columnsSettings);

        if (!aSettings || !bSettings) {
            return (
                defaultGridColumnsOrdering.indexOf(a.field) -
                defaultGridColumnsOrdering.indexOf(b.field)
            );
        }
        return aSettings.index - bSettings.index;
    });

    return shownColumns;
};

export default getImputationsColumns;
